<template>
  <ModalOverlay @click="closeModal">
    <ResponsiveModalContainer @click.stop>
      <ResponsiveModalHeader>
        <ResponsiveModalTitle>
          <MapMarker :size="24" :style="{ paddingTop: '4px' }" />위치별 품목 등록
        </ResponsiveModalTitle>
        <CloseButton @click="closeModal">
          <span>&times;</span>
        </CloseButton>
      </ResponsiveModalHeader>

      <ResponsiveModalBody>
        <!-- * 위치 정보 표시 -->
        <LocationInfo>
          <LocationBadge>
            <LocationLabel>등록위치</LocationLabel>
            <LocationValue>{{ displayTargetLocation }}</LocationValue>
          </LocationBadge>
        </LocationInfo>

        <!-- * 탭 메뉴 -->
        <TabContainer>
          <TabButton :class="{ active: activeTab === 'manual' }" @click="activeTab = 'manual'">
            <TabIcon>✏️</TabIcon>
            수기 입력
          </TabButton>
          <TabButton :class="{ active: activeTab === 'existing' }" @click="activeTab = 'existing'">
            <TabIcon>📋</TabIcon>
            기존 품목 수정
          </TabButton>
        </TabContainer>

        <!-- * 수기 입력 탭 -->
        <TabContent v-if="activeTab === 'manual'">
          <FormSection>
            <SectionTitleContainer>
              <SectionTitle>
                품목 정보 입력
              </SectionTitle>
              <BarcodeScanStatusButton @click="toggleBarcodeScanning('manual')"
                :class="{ scanning: isBarcodeScanning && activeBarcodeInput === 'manual' }">
                <span class="scan-text">
                  {{ (isBarcodeScanning && activeBarcodeInput === 'manual') ? '스캔 중지' : '바코드 스캔' }}
                </span>
              </BarcodeScanStatusButton>
            </SectionTitleContainer>

            <ResponsiveFormGrid>

              <!-- * 선택된 이미지 미리보기 -->
              <FormGroup v-if="selectedProductImage" class="selected-image-group">
                <FormLabel>선택된 품목 이미지</FormLabel>
                <SelectedImageContainer>
                  <SelectedImagePreview>
                    <SelectedImage :src="selectedProductImage.thumbnail" :alt="selectedProductImage.title" />
                    <ImageLoadingOverlay v-if="imageProcessing" class="image-loading">
                      <LoadingSpinner>이미지 처리 중...</LoadingSpinner>
                    </ImageLoadingOverlay>
                  </SelectedImagePreview>
                  <SelectedImageInfo>
                    <SelectedImageTitle>{{ truncateTitle(selectedProductImage.title) }}</SelectedImageTitle>
                    <SelectedImageSize>{{ selectedProductImage.width }} × {{ selectedProductImage.height }}
                    </SelectedImageSize>
                    <SelectedImageUrl>{{ truncateTitle(selectedProductImage.url) }}</SelectedImageUrl>
                    <ImageActionButtons>
                      <RemoveImageButton @click="removeSelectedImage" title="이미지 제거">
                        🗑️ 제거
                      </RemoveImageButton>
                    </ImageActionButtons>
                  </SelectedImageInfo>
                </SelectedImageContainer>
              </FormGroup>

              <FormGroup>
                <FormLabel class="required">카테고리1</FormLabel>
                <FormSelect v-model="selectedCategory1" @change="onCategory1Change" required>
                  <option value="">선택하세요</option>
                  <option v-for="category in category1List" :key="category.num" :value="category.num">
                    {{ category.name }}
                  </option>
                </FormSelect>
              </FormGroup>

              <FormGroup>
                <FormLabel class="required">카테고리2</FormLabel>
                <FormSelect v-model="selectedCategory2" :disabled="!selectedCategory1" required>
                  <option value="">선택하세요</option>
                  <option v-for="category in category2List" :key="category.num" :value="category.num">
                    {{ category.name }}
                  </option>
                </FormSelect>
              </FormGroup>

              <FormGroup>
                <FormLabel class="required">품목명</FormLabel>
                <ProductNameInputContainer>
                  <FormInput v-model="manualProduct.name3" type="text" placeholder="품목명을 입력하세요" required
                    @keydown.enter="searchProductImages" />
                  <ImageSearchButton @click="searchProductImages" :disabled="!manualProduct.name3.trim()"
                    title="이미지 검색">
                    <Magnify :size="20" />
                  </ImageSearchButton>
                </ProductNameInputContainer>
              </FormGroup>

              <FormGroup>
                <FormLabel class="required">바코드</FormLabel>
                <FormInput id="manual-barcode-input" v-model="manualProduct.bcode" type="text"
                  placeholder="바코드를 스캔하거나 입력하세요" required :class="[
                    'barcode-form-input',
                    { scanning: isBarcodeScanning && activeBarcodeInput === 'manual' }
                  ]" @focus="setActiveBarcodeInput('manual')" @blur="clearActiveBarcodeInput" />
              </FormGroup>

              <FormGroup>
                <FormLabel class="required">재고위치</FormLabel>
                <FormStockInputContainer class="form-stock-input-container">
                  <FormStockInput v-model="manualProduct.location1" type="text" placeholder="가"
                    @input="updateManualLocation" required class="location-input" />
                  <FormStockSeparator>-</FormStockSeparator>
                  <FormStockInput v-model="manualProduct.location2" type="text" placeholder="1"
                    @input="updateManualLocation" required class="location-input" />
                </FormStockInputContainer>
              </FormGroup>

              <FormGroup>
                <FormLabel class="required">수량 / 낱개수량</FormLabel>
                <FormStockInputContainer>
                  <FormStockInput v-model.number="manualProduct.quantity" placeholder="품목수량"
                    required />
                  <span style="margin: 0 8px; color: #666; font-weight: 500;">/</span>
                  <FormStockInput v-model.number="manualProduct.piece_quantity" placeholder="낱개수량"
                    required />
                </FormStockInputContainer>
              </FormGroup>

              <FormGroup>
                <FormLabel>모델명</FormLabel>
                <FormInput v-model="manualProduct.name4" type="text" placeholder="제품명을 입력하세요" required />
              </FormGroup>

              <FormGroup>
                <FormLabel>품목코드</FormLabel>
                <FormInput v-model="manualProduct.code" type="text" placeholder="품목코드 입력하세요" required />
              </FormGroup>

              <FormGroup>
                <FormLabel>사이즈</FormLabel>
                <FormInput v-model="manualProduct.size" type="text" placeholder="사이즈를 입력하세요" required />
              </FormGroup>

              <FormGroup>
                <FormLabel>색상</FormLabel>
                <FormSelect v-model="manualProduct.color">
                  <option value="">선택하세요</option>
                  <option v-for="color in colorList" :key="color.num" :value="color.name">
                    {{ color.name }}
                  </option>
                </FormSelect>
              </FormGroup>

              <FormGroup>
                <FormLabel>거래처</FormLabel>
                <FormInput v-model="manualProduct.company" type="text" placeholder="제조사명을 입력하세요" />
              </FormGroup>

              <FormGroup>
                <FormLabel>구입가격</FormLabel>
                <FormInput v-model.number="manualProduct.price" type="number" min="0" placeholder="구입가격을 입력하세요" />
              </FormGroup>

              <FormGroup>
                <FormLabel>대여가격</FormLabel>
                <FormInput v-model.number="manualProduct.price2" type="number" min="0" placeholder="대여가격을 입력하세요" />
              </FormGroup>

              <FormGroup>
                <FormLabel>공급가격</FormLabel>
                <FormInput v-model.number="manualProduct.price3" type="number" min="0" placeholder="공급가격 입력하세요" />
              </FormGroup>

              <FormGroup>
                <FormLabel>대상</FormLabel>
                <FormSelect v-model="manualProduct.target" placeholder="대상을 선택하세요">
                  <option value="">선택하세요</option>
                  <option value="구입">구입</option>
                  <option value="대여">대여</option>
                  <option value="구입 또는 대여">구입 또는 대여</option>
                </FormSelect>
              </FormGroup>

              <FormGroup>
                <FormLabel>과세</FormLabel>
                <FormSelect v-model="manualProduct.tax" placeholder="과세 여부를 선택하세요">
                  <option value="">선택하세요</option>
                  <option value="과세">과세</option>
                  <option value="영세">영세</option>
                </FormSelect>
              </FormGroup>

              <FormGroup>
                <FormLabel>보험코드</FormLabel>
                <FormInput v-model="manualProduct.icode" type="text" placeholder="보험코드를 입력하세요" />
              </FormGroup>

              <FormGroup>
                <FormLabel>비고</FormLabel>
                <FormInput v-model="manualProduct.note" type="text" placeholder="비고를 입력하세요" />
              </FormGroup>

            </ResponsiveFormGrid>
          </FormSection>
        </TabContent>

        <!-- * 기존 품목 선택 탭 -->
        <TabContent v-if="activeTab === 'existing'">
          <SearchSection>
            <SearchContainer>
              <SearchInput v-model="searchQuery" type="text" placeholder="품목명을 검색하세요" @input="searchProducts" />
              <SearchButton @click="searchProducts">
                <Magnify :size="24" />
              </SearchButton>
            </SearchContainer>
          </SearchSection>

          <ProductsSection>
            <SectionTitleContainer>
              <SectionTitle>
                바코드 없는 품목 목록
                <span v-if="selectedProducts.length === 0">
                  ({{ filteredProducts.length }}개)
                </span>
                <span v-if="selectedProducts.length > 0" style="color: #cc5803;">
                  ({{ selectedProducts.length }}개 선택됨)
                </span>
              </SectionTitle>
              <BarcodeScanStatusButton @click="toggleBarcodeScanning('existing')"
                :class="{ scanning: isBarcodeScanning && activeBarcodeInput === 'existing' }">
                <span class="scan-text">
                  {{ (isBarcodeScanning && activeBarcodeInput === 'existing') ? '스캔 중지' : '바코드 스캔' }}
                </span>
              </BarcodeScanStatusButton>
            </SectionTitleContainer>

            <LoadingContainer v-if="loading">
              <LoadingSpinner></LoadingSpinner>
              <p>품목을 검색하고 있습니다...</p>
            </LoadingContainer>

            <EmptyState v-else-if="filteredProducts.length === 0">
              <EmptyIcon>📦</EmptyIcon>
              <p>검색 결과가 없습니다.</p>
              <small>다른 검색어를 입력해보세요.</small>
            </EmptyState>

            <ProductTableContainer v-else>
              <ProductTable>
                <ProductTableHeader>
                  <SelectAllContainer>
                    <CustomCheckbox type="checkbox" :checked="isAllSelected" @change="toggleSelectAll" id="selectAll" />
                    <SelectAllLabel for="selectAll"></SelectAllLabel>
                  </SelectAllContainer>
                  <div>카테고리1</div>
                  <div>카테고리2</div>
                  <div>품목명</div>
                  <div>바코드</div>
                  <div>재고위치</div>
                </ProductTableHeader>

                <ProductTableRow v-for="product in filteredProducts" :key="product.id"
                  :class="{ selected: selectedProducts.includes(product.id) }">
                  <!-- 선택 체크박스 -->
                  <CheckboxContainer>
                    <CustomCheckbox type="checkbox" :checked="selectedProducts.includes(product.id)"
                      @change="toggleProductSelection(product.id)" />
                  </CheckboxContainer>

                  <!-- 카테고리1 (name0) -->
                  <ProductTableCell>
                    <ProductTableInput v-model="product.name0" @input="updateProduct(product)" placeholder="카테고리1" />
                  </ProductTableCell>

                  <!-- 카테고리2 (name1) -->
                  <ProductTableCell>
                    <ProductTableInput v-model="product.name1" @input="updateProduct(product)" placeholder="카테고리2" />
                  </ProductTableCell>

                  <!-- 품목명 (name3) -->
                  <ProductTableCell>
                    <ProductTableInput v-model="product.name3" @input="updateProduct(product)" placeholder="품목명" />
                  </ProductTableCell>

                  <!-- 바코드 (bcode) -->
                  <ProductTableCell>
                    <ProductTableInput :id="`existing-barcode-input-${product.id}`" v-model="product.bcode"
                      @input="updateProduct(product)" placeholder="바코드를 스캔하거나 입력하세요" :class="[
                        'barcode-product-table-input',
                        { scanning: isBarcodeScanning && activeBarcodeInput === 'existing' && currentScanProduct?.id === product.id }
                      ]" @focus="setActiveBarcodeInput('existing', product)" @blur="clearActiveBarcodeInput" />
                  </ProductTableCell>

                  <!-- 위치 (location) -->
                  <ProductTableCell>
                    <LocationInputContainer>
                      <LocationInput v-model="product.location1" @input="updateProductLocation(product)" />
                      <LocationSeparator>-</LocationSeparator>
                      <LocationInput v-model="product.location2" @input="updateProductLocation(product)" />
                    </LocationInputContainer>
                  </ProductTableCell>
                </ProductTableRow>
              </ProductTable>
            </ProductTableContainer>
          </ProductsSection>
        </TabContent>
      </ResponsiveModalBody>

      <ModalFooter>
        <ConfirmButton @click="activeTab === 'manual' ? registerProduct() : saveProductsBatch()"
          :disabled="!canRegister">
          {{ activeTab === 'manual' ? '등록' : '저장' }}
        </ConfirmButton>
        <CancelButton @click="closeModal"> 취소 </CancelButton>
      </ModalFooter>
    </ResponsiveModalContainer>

    <!-- * 이미지 검색 모달 -->
    <ImageSearchModal v-if="showImageSearchModal" :initialSearchQuery="manualProduct.name3"
      @close="showImageSearchModal = false" @imageSelected="onImageSelected" />

    <!-- * 이미지 미리보기 모달 -->
    <div v-if="showImagePreview" class="image-preview-modal" @click="showImagePreview = false">
      <div class="image-preview-container" @click.stop>
        <img :src="selectedProductImage?.url" :alt="selectedProductImage?.title" class="preview-full-image" />
        <button class="close-preview" @click="showImagePreview = false">&times;</button>
      </div>
    </div>
  </ModalOverlay>
</template>

<script>
import * as LocationAddModalCss from "@/assets/styles/product/modal/LocationAddModalCss.js";
import ImageSearchModal from './ImageSearchModal.vue';

import Magnify from "vue-material-design-icons/Magnify.vue";
import MapMarker from "vue-material-design-icons/MapMarker.vue";

export default {
  name: "LocationAddModal",

  components: {
    ...LocationAddModalCss,
    ImageSearchModal,
    Magnify, MapMarker
  },

  props: {
    targetLocation: {
      type: String,
      required: true,
    },
    initialBarcode: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      activeTab: "manual",
      loading: false,
      searchQuery: "",

      // 수기 입력 데이터
      manualProduct: {
        name0: "",
        name1: "",
        name3: "",
        name4: "",
        code: "",
        bcode: "",
        location: "",
        location1: "",
        location2: "",
        quantity: "",
        piece_quantity: "",
        size: "",
        color: "",
        company: "",
        price: 0,
        price2: 0,
        price3: 0,
        target: "",
        tax: "",
        icode: "",
        note: "",
      },

      // 기존 품목 데이터
      allProducts: [],
      filteredProducts: [],
      selectedProducts: [], // 선택된 제품 ID 배열
      updatedProducts: new Set(), // 수정된 제품 추적

      // 카테고리
      category1List: [],
      category2List: [],
      allCategories: [],
      selectedCategory1: '',
      selectedCategory2: '',

      // 색상 목록
      colorList: [],

      // 바코드 스캐너 관련
      isBarcodeScanning: false, // 바코드 스캔 모드 활성화 여부
      activeBarcodeInput: null, // 현재 활성화된 바코드 입력 필드 ('manual' 또는 'existing')
      currentScanProduct: null, // 현재 스캔 중인 제품 (기존 품목용)
      scannedBarcode: "",
      barcodeInputBuffer: "", // 바코드 입력 버퍼
      lastInputTime: 0, // 마지막 입력 시간 (바코드 스캐너 감지용)

      // 이미지 검색 관련
      showImageSearchModal: false,
      selectedProductImage: null,
      imageFile: null, // 실제 업로드할 이미지 파일
      imageProcessing: false, // 이미지 처리 중 상태
      showImagePreview: false, // 이미지 미리보기 모달
    };
  },

  computed: {
    // 등록위치 표시용 computed 속성
    displayTargetLocation() {
      // targetLocation이 존재하지 않거나 유효하지 않은 경우
      if (!this.targetLocation) {
        return '없음';
      }

      // 문자열로 변환하여 체크
      const locationStr = String(this.targetLocation);

      // 유효하지 않은 값들 체크
      if (locationStr === '[object PointerEvent]' ||
        locationStr.includes('[object') ||
        locationStr.trim() === '' ||
        locationStr === 'undefined' ||
        locationStr === 'null') {
        return '없음';
      }

      return locationStr;
    },

    // 등록 가능 여부
    canRegister() {
      if (this.activeTab === 'manual') {
        return this.manualProduct.name3.trim() !== '' &&
          this.manualProduct.bcode.trim() !== '' &&
          this.manualProduct.location1.trim() !== '' &&
          this.manualProduct.location2.trim() !== '' &&
          this.manualProduct.quantity > 0 &&
          this.selectedCategory1 !== '' &&
          this.selectedCategory2 !== '';
      } else {
        return this.selectedProducts.length > 0;
      }
    },

    // 전체 선택 상태 확인
    isAllSelected() {
      return this.filteredProducts.length > 0 &&
        this.selectedProducts.length === this.filteredProducts.length;
    }
  },

  async mounted() {
    await this.getCategoryList();
    await this.getColorTable();
    await this.getProductsWithoutBarcode();

    // 초기 바코드 정보가 있으면 수기 입력 필드에 설정
    if (this.initialBarcode) {
      this.manualProduct.bcode = this.initialBarcode;

      // 재고 위치도 동시에 설정 (displayTargetLocation 사용)
      const locationStr = this.displayTargetLocation;
      if (locationStr && locationStr !== '없음') {
        const locationParts = locationStr.split('-');
        this.manualProduct.location1 = locationParts[0] || locationStr;
        this.manualProduct.location2 = locationParts[1] || '';
        this.updateManualLocation(); // location 필드 업데이트
      }

      // 바코드와 위치가 모두 입력된 경우 알림 표시
      this.$nextTick(() => {
        this.$swal.fire({
          title: '자동 입력 완료',
          html: `
            <p><strong>바코드:</strong> ${this.initialBarcode}</p>
            <p><strong>재고위치:</strong> ${locationStr}</p>
            <p>스캔된 정보가 자동으로 입력되었습니다.</p>
          `,
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      });
    } else {
      // 바코드는 없지만 위치 정보만 있는 경우
      const locationStr = this.displayTargetLocation;
      if (locationStr && locationStr !== '없음') {
        const locationParts = locationStr.split('-');
        this.manualProduct.location1 = locationParts[0] || locationStr;
        this.manualProduct.location2 = locationParts[1] || '';
        this.updateManualLocation(); // location 필드 업데이트

        this.$nextTick(() => {
          this.$swal.fire({
            title: '재고위치 입력 완료',
            html: `
              <p><strong>재고위치:</strong> ${locationStr}</p>
              <p>재고위치가 자동으로 입력되었습니다.</p>
            `,
            icon: 'info',
            timer: 2000,
            showConfirmButton: false
          });
        });
      }
    }

    document.addEventListener("keydown", this.handleBarcodeInput);
    document.addEventListener("keydown", this.handleEscKey);
  },

  beforeUnmount() {
    document.removeEventListener("keydown", this.handleBarcodeInput);
    document.removeEventListener("keydown", this.handleEscKey);
  },

  methods: {
    // 이미지 검색 모달 열기
    searchProductImages() {
      if (!this.manualProduct.name3.trim()) {
        this.$swal.fire('알림', '품목명을 입력해주세요.', 'warning');
        return;
      }

      this.showImageSearchModal = true;
    },

    // 이미지 선택 처리
    async onImageSelected(imageData) {
      console.log('선택된 이미지 데이터:', imageData);

      try {
        this.imageProcessing = true;
        this.selectedProductImage = imageData;

        this.$swal.fire({
          title: '이미지 선택 완료',
          text: '품목 이미지가 선택되었습니다.',
          icon: 'success',
          timer: 1000,
          showConfirmButton: false
        });

      } catch (error) {
        console.error('이미지 처리 중 오류:', error);
        this.$swal.fire({
          title: '이미지 처리 실패',
          text: '이미지를 처리하는 중 오류가 발생했습니다. 다른 이미지를 선택해주세요.',
          icon: 'error'
        });
        this.removeSelectedImage();
      } finally {
        this.imageProcessing = false;
      }
    },

    // 선택된 이미지 제거
    removeSelectedImage() {
      this.selectedProductImage = null;
      this.imageFile = null;
      this.imageProcessing = false;
    },

    // 전체 선택/해제
    toggleSelectAll() {
      if (this.isAllSelected) {
        // 전체 해제
        this.selectedProducts = [];
      } else {
        // 전체 선택
        this.selectedProducts = this.filteredProducts.map(product => product.id);
      }
    },

    // 제품 선택/해제
    toggleProductSelection(productId) {
      const index = this.selectedProducts.indexOf(productId);
      if (index > -1) {
        this.selectedProducts.splice(index, 1);
      } else {
        this.selectedProducts.push(productId);
      }
    },

    // 수기 입력 위치 업데이트
    updateManualLocation() {
      const location1 = (this.manualProduct.location1 || '').trim();
      const location2 = (this.manualProduct.location2 || '').trim();

      if (location1 && location2) {
        this.manualProduct.location = `${location1}-${location2}`;
      } else if (location1) {
        this.manualProduct.location = location1;
      } else if (location2) {
        this.manualProduct.location = location2;
      } else {
        this.manualProduct.location = '';
      }
    },

    // 색상 목록 로드
    async getColorTable() {
      try {
        const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/ColorTableGet`, {});
        const data = response.data;

        if (data.code === 200) {
          // 배열 형태로 저장하도록 수정
          this.colorList = data.data.map(color => ({
            num: color.num,
            name: color.name
          }));
        } else {
          throw new Error('색상 데이터 로드 실패');
        }
      } catch (error) {
        console.error('색상 테이블 가져오기 실패:', error);

        // 에러 발생 시 기본 색상 목록 사용
        this.colorList = [
          { num: 1, name: '블랙' },
          { num: 2, name: '화이트' },
          { num: 3, name: '레드' },
          { num: 4, name: '블루' },
          { num: 5, name: '그린' },
          { num: 6, name: '옐로우' },
          { num: 7, name: '그레이' },
          { num: 8, name: '브라운' }
        ];

        console.warn('기본 색상 목록을 사용합니다.');
      }
    },

    // 카테고리 목록 로드
    async getCategoryList() {
      try {
        const response = await this.$axios.get(
          `${this.$apiUrls.hanqmedi}/getAllCategories`,
          {
            params: {
              companykey: this.$globalState.company_key
            }
          }
        );

        if (response.data) {
          this.allCategories = response.data.data;
          this.category1List = this.allCategories;
        }
      } catch (error) {
        console.error('카테고리 목록 로드 오류:', error);
        this.$swal.fire('오류', '카테고리 목록을 불러오는데 실패했습니다.', 'error');
      }
    },

    // 카테고리1 선택 시 해당하는 카테고리2 목록 조회
    async onCategory1Change() {
      try {
        this.selectedCategory2 = '';
        this.category2List = [];

        if (!this.selectedCategory1) return;

        // 선택된 카테고리1의 하위 카테고리 찾기
        const selectedCategory = this.category1List.find(
          cat => cat.num === parseInt(this.selectedCategory1)
        );

        if (selectedCategory && selectedCategory.subcategories) {
          this.category2List = selectedCategory.subcategories;
        }
      } catch (error) {
        console.error('카테고리2 목록 업데이트 중 오류 발생:', error);
        this.$swal.fire({
          title: '오류',
          text: '카테고리2 목록을 불러오는데 실패했습니다.',
          icon: 'error',
          confirmButtonColor: '#fb8b24'
        });
      }
    },

    // 제품 정보 업데이트
    updateProduct(product) {
      this.updatedProducts.add(product.id);
    },

    async getProductsWithoutBarcode() {
      try {
        this.loading = true;

        const params = {
          companykey: this.$globalState.company_key,
          search: this.searchQuery || '',
        };

        const response = await this.$axios.get(
          `${this.$apiUrls.hanqmedi}/getProductsWithoutBarcode`,
          { params }
        );

        if (response.data && response.data.code === 200) {
          this.allProducts = response.data.data.map((product) => {
            const locationParts = (product.location || '').split('-');
            return {
              ...product,
              selectedQuantity: 1,
              location1: locationParts[0] || '',
              location2: locationParts[1] || '',
            };
          });
          this.filteredProducts = [...this.allProducts];

          if (response.data.meta) {
            console.log('검색 결과:', response.data.meta);
          }
        } else {
          throw new Error('응답 데이터 형식이 올바르지 않습니다.');
        }
      } catch (error) {
        console.error("바코드 없는 품목 로드 오류:", error);

        let errorMessage = "품목 목록을 불러오는데 실패했습니다.";

        if (error.response) {
          if (error.response.status === 400) {
            errorMessage = "회사 정보가 올바르지 않습니다.";
          } else if (error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        }

        this.$swal.fire("오류", errorMessage, "error");
      } finally {
        this.loading = false;
      }
    },

    // 위치 업데이트 전용 메서드
    updateProductLocation(product) {
      // location1과 location2를 합쳐서 location에 저장
      const location1 = (product.location1 || '').trim();
      const location2 = (product.location2 || '').trim();

      if (location1 && location2) {
        product.location = `${location1}-${location2}`;
      } else if (location1) {
        product.location = location1;
      } else if (location2) {
        product.location = location2;
      } else {
        product.location = '';
      }

      // 일반 업데이트 메서드도 호출
      this.updateProduct(product);
    },


    async searchProducts() {
      // 실시간 검색을 위해 백엔드 API 호출
      if (this.searchQuery.trim() === '') {
        await this.getProductsWithoutBarcode();
        return;
      }

      try {
        this.loading = true;

        const params = {
          companykey: this.$globalState.company_key,
          search: this.searchQuery.trim(),
        };

        const response = await this.$axios.get(
          `${this.$apiUrls.hanqmedi}/getProductsWithoutBarcode`,
          { params }
        );

        if (response.data && response.data.code === 200) {
          this.filteredProducts = response.data.data.map((product) => ({
            ...product,
            selectedQuantity: 1,
          }));
        }
      } catch (error) {
        console.error("검색 중 오류:", error);
      } finally {
        this.loading = false;
      }
    },

    selectProduct(product) {
      this.selectedProduct = this.selectedProduct?.id === product.id ? null : product;
    },

    // 수기 입력 등록 메서드
    async registerProduct() {
      try {
        const formData = new FormData();

        // 선택된 카테고리1과 카테고리2의 이름을 찾음
        const category1 = this.category1List.find(cat => cat.num === parseInt(this.selectedCategory1));
        const category2 = category1?.subcategories?.find(cat => cat.num === parseInt(this.selectedCategory2));

        // 카테고리 이름을 manualProduct에 먼저 설정
        this.manualProduct.name0 = category1?.name || '';
        this.manualProduct.name1 = category2?.name || '';

        // 최종 위치 정보 업데이트
        this.updateManualLocation();

        // 선택된 이미지가 있으면 이미지 URL과 메타데이터를 전송
        if (this.selectedProductImage) {
          formData.append('imageUrl', this.selectedProductImage.url);
          formData.append('imageTitle', this.selectedProductImage.title || '');
          formData.append('imageWidth', this.selectedProductImage.width || '');
          formData.append('imageHeight', this.selectedProductImage.height || '');
          console.log('이미지 URL이 FormData에 추가되었습니다:', this.selectedProductImage.url);
        }

        // 모든 제품 정보를 formData에 추가 (location1, location2 제외)
        Object.keys(this.manualProduct).forEach(key => {
          if (key !== 'location1' && key !== 'location2') {
            formData.append(key, this.manualProduct[key] || '');
          }
        });

        // 카테고리 ID도 함께 전송
        formData.append('category1_id', this.selectedCategory1);
        formData.append('category2_id', this.selectedCategory2);

        // 회사키 추가
        formData.append('companykey', this.$globalState.company_key);

        const response = await this.$axios.post(
          `${this.$apiUrls.hanqmedi}/registerProducts`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        if (response.status === 201 || response.status === 200) {
          // 등록된 품목 정보 준비
          const registeredProduct = {
            id: response.data.productId || Date.now(), // 서버에서 반환된 ID 또는 임시 ID
            name0: this.manualProduct.name0,
            name1: this.manualProduct.name1,
            name3: this.manualProduct.name3,
            name4: this.manualProduct.name4,
            code: this.manualProduct.code,
            bcode: this.manualProduct.bcode,
            location: this.manualProduct.location,
            location1: this.manualProduct.location1,
            location2: this.manualProduct.location2,
            quantity: this.manualProduct.quantity,
            piece_quantity: this.manualProduct.piece_quantity,
            size: this.manualProduct.size,
            color: this.manualProduct.color,
            company: this.manualProduct.company,
            price: this.manualProduct.price,
            price2: this.manualProduct.price2,
            price3: this.manualProduct.price3,
            target: this.manualProduct.target,
            tax: this.manualProduct.tax,
            icode: this.manualProduct.icode,
            note: this.manualProduct.note,
            imageUrl: this.selectedProductImage?.url || null,
            category1_id: this.selectedCategory1,
            category2_id: this.selectedCategory2
          };

          await this.$swal.fire({
            title: '품목 등록 완료',
            html: `
              <p><strong>품목명:</strong> ${this.manualProduct.name3}</p>
              <p><strong>재고위치:</strong> ${this.manualProduct.location}</p>
              <p><strong>수량:</strong> ${this.manualProduct.quantity}개</p>
              <p style="color: #0f4c5c; font-weight: bold;">성공적으로 등록되었습니다!</p>
            `,
            icon: 'success',
            confirmButtonColor: '#0f4c5c',
            confirmButtonText: '확인'
          });

          // 부모 컴포넌트에게 등록된 품목 정보와 위치 정보 전달
          this.$emit('productRegistered', {
            product: registeredProduct,
            targetLocation: this.displayTargetLocation,
            location: this.manualProduct.location
          });

          this.resetManualForm();
          this.closeModal();
        }
      } catch (error) {
        console.error('품목 등록 오류:', error);

        let errorMessage = '품목 등록 중 오류가 발생했습니다.';
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }

        this.$swal.fire('오류', errorMessage, 'error');
      }
    },

    // 이미지 URL에서 Blob 다운로드
    async downloadImageAsBlob(imageUrl) {
      try {
        // CORS를 우회하기 위해 프록시 서버 사용 (필요한 경우)
        const proxyUrl = imageUrl.startsWith('http') ? imageUrl : `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(imageUrl)}`;

        const response = await fetch(proxyUrl, {
          method: 'GET',
          headers: {
            'Accept': 'image/*'
          }
        });

        if (!response.ok) {
          throw new Error(`이미지 다운로드 실패: ${response.status}`);
        }

        return await response.blob();
      } catch (error) {
        console.error('이미지 다운로드 오류:', error);

        // 대체 방법: Canvas를 사용한 이미지 변환
        try {
          return await this.convertImageToBlob(imageUrl);
        } catch (canvasError) {
          console.error('Canvas 변환도 실패:', canvasError);
          throw new Error('이미지 변환에 실패했습니다.');
        }
      }
    },

    // Canvas를 사용한 이미지 변환 (CORS 대안)
    async convertImageToBlob(imageUrl) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            canvas.width = img.width;
            canvas.height = img.height;

            ctx.drawImage(img, 0, 0);

            canvas.toBlob((blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Canvas에서 Blob 생성 실패'));
              }
            }, 'image/jpeg', 0.8);
          } catch (error) {
            reject(error);
          }
        };

        img.onerror = () => {
          reject(new Error('이미지 로드 실패'));
        };

        img.src = imageUrl;
      });
    },

    // 이미지 URL에서 확장자 추출
    getImageExtension(url) {
      try {
        const urlParts = url.split('.');
        const extension = urlParts[urlParts.length - 1].split('?')[0].toLowerCase();

        // 지원하는 이미지 확장자인지 확인
        const supportedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
        return supportedExtensions.includes(extension) ? extension : 'jpg';
      } catch (error) {
        console.error('확장자 추출 오류:', error);
        return 'jpg';
      }
    },

    // 수기 입력 폼 초기화 (기존 메서드 개선)
    resetManualForm() {
      this.manualProduct = {
        name0: '',
        name1: '',
        name3: '',
        name4: '',
        code: '',
        bcode: '',
        location: '',
        location1: '',
        location2: '',
        quantity: 1,
        piece_quantity: 0,
        size: '',
        color: '',
        company: '',
        price: 0,
        price2: 0,
        price3: 0,
        target: '',
        tax: '',
        icode: '',
        note: '',
      };
      this.selectedCategory1 = '';
      this.selectedCategory2 = '';
      this.category2List = [];
      this.selectedProductImage = null;
    },

    // 기존 품목 저장
    async saveProductsBatch() {
      try {
        const selectedProductsData = this.filteredProducts.filter(product =>
          this.selectedProducts.includes(product.id)
        );

        if (selectedProductsData.length === 0) {
          this.$swal.fire('알림', '저장할 품목을 선택해주세요.', 'warning');
          return;
        }

        const confirmResult = await this.$swal.fire({
          title: '저장 확인',
          text: `선택된 ${selectedProductsData.length}개 품목을 저장하시겠습니까?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#0f4c5c',
          cancelButtonColor: '#6c757d',
          confirmButtonText: '저장',
          cancelButtonText: '취소'
        });

        if (!confirmResult.isConfirmed) {
          return;
        }

        // 로딩 시작
        this.$swal.fire({
          title: '저장 중...',
          html: `
            <div style="display: flex; flex-direction: column; align-items: center; gap: 16px;">
              <p style="margin: 0; color: #0f4c5c; font-weight: 600;">
                ${selectedProductsData.length}개 품목을 저장하고 있습니다...
              </p>
            </div>
          `,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            this.$swal.showLoading();
          }
        });

        // 업데이트할 제품들의 정보를 준비
        const productsToUpdate = selectedProductsData.map(product => {
          // location1과 location2를 합쳐서 location 생성
          const location = product.location1 && product.location2
            ? `${product.location1}-${product.location2}`
            : (product.location1 || product.location2 || '');

          return {
            id: product.id,
            name0: product.name0,
            name1: product.name1,
            name3: product.name3,
            name4: product.name4,
            code: product.code,
            bcode: product.bcode,
            size: product.size,
            color: product.color,
            target: product.target,
            price: product.price,
            price2: product.price2,
            price3: product.price3,
            quantity: product.quantity,
            piece_quantity: product.piece_quantity,
            tax: product.tax,
            icode: product.icode,
            location: location,
            company: product.company,
            note: product.note
          };
        });

        // JSON 형태로 데이터 전송
        const requestData = {
          products: productsToUpdate,
          companykey: this.$globalState.company_key
        };

        const response = await this.$axios.post(
          `${this.$apiUrls.hanqmedi}/updateProductsBatch`,
          requestData,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        // 로딩 종료 후 성공 메시지
        if (response.data.success || response.status === 200 || response.status === 201) {
          await this.$swal.fire({
            title: '저장 완료',
            html: `
              <div style="display: flex; flex-direction: column; align-items: center; gap: 12px;">
                <p style="margin: 0;"><strong>저장된 품목 수:</strong> ${selectedProductsData.length}개</p>
                <p style="margin: 0; color: #0f4c5c; font-weight: 600;">성공적으로 저장되었습니다!</p>
              </div>
            `,
            icon: 'success',
            confirmButtonColor: '#0f4c5c',
            confirmButtonText: '확인',
          });

          // 부모 컴포넌트에게 저장된 품목들 정보 전달
          this.$emit('productsSaved', {
            products: productsToUpdate,
            targetLocation: this.displayTargetLocation,
            count: selectedProductsData.length
          });

          this.selectedProducts = [];
          this.updatedProducts.clear();
          await this.getProductsWithoutBarcode();
        }

      } catch (error) {
        console.error('품목 일괄 저장 오류:', error);

        let errorMessage = '품목 저장 중 오류가 발생했습니다.';
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }

        // 에러 발생 시 로딩 종료 후 에러 메시지 표시
        await this.$swal.fire({
          title: '저장 실패',
          html: `
            <div style="display: flex; flex-direction: column; align-items: center; gap: 12px;">
              <p style="margin: 0; color: #dc3545;">${errorMessage}</p>
            </div>
          `,
          icon: 'error',
          confirmButtonColor: '#dc3545',
          confirmButtonText: '확인'
        });
      }
    },

    // 바코드 관련 =================================================================
    toggleBarcodeScanning(type) {
      if (this.isBarcodeScanning && this.activeBarcodeInput === type) {
        this.disableBarcodeScanning();
      } else {
        this.enableBarcodeScanning(type);
      }
    },

    // 바코드 스캔 모드 활성화
    enableBarcodeScanning(type) {
      this.isBarcodeScanning = true;
      this.activeBarcodeInput = type;
      this.barcodeInputBuffer = "";

      // 해당 탭의 바코드 입력 필드에 포커스
      this.$nextTick(() => {
        if (type === 'manual') {
          // 수기 입력 탭의 바코드 필드에 포커스
          const input = document.getElementById('manual-barcode-input');
          if (input) {
            input.focus();
          }
        } else if (type === 'existing') {
          // 기존 품목 탭에서는 사용자가 직접 포커스할 때까지 대기
          this.currentScanProduct = null;
        }
      });
    },

    // 바코드 스캔 모드 비활성화
    disableBarcodeScanning() {
      this.isBarcodeScanning = false;
      this.activeBarcodeInput = null;
      this.currentScanProduct = null;
      this.barcodeInputBuffer = "";
    },

    setActiveBarcodeInput(type, product = null) {
      if (!this.isBarcodeScanning) {
        return;
      }

      // 현재 활성화된 스캔 모드와 일치하는 경우에만 처리
      if (this.activeBarcodeInput === type) {
        if (type === 'existing') {
          // 기존 품목 모드에서는 포커스된 품목을 현재 스캔 대상으로 설정
          this.currentScanProduct = product;
        }
        this.barcodeInputBuffer = "";
      }
    },

    // 활성 바코드 입력 필드 해제
    clearActiveBarcodeInput() {
      setTimeout(() => {
        const activeElement = document.activeElement;
        if (!activeElement ||
          (!activeElement.placeholder?.includes('바코드') &&
            !activeElement.classList?.contains('barcode-form-input') &&
            !activeElement.classList?.contains('barcode-product-table-input'))) {

          // 기존 품목 모드에서 바코드 필드가 아닌 곳으로 포커스가 이동하면 현재 스캔 대상 해제
          if (this.activeBarcodeInput === 'existing') {
            this.currentScanProduct = null;
          }
          this.barcodeInputBuffer = "";
        }
      }, 100);
    },

    // 바코드 입력 처리
    handleBarcodeInput(event) {
      // 바코드 스캔 모드가 비활성화되어 있으면 특정 조건에서만 차단
      if (!this.isBarcodeScanning || !this.activeBarcodeInput) {
        // 현재 포커스된 요소 확인
        const activeElement = document.activeElement;

        // 일반 입력 필드들은 차단하지 않음 (위치, 품목명, 사이즈 등)
        const isGeneralInputField = activeElement && (
          activeElement.closest('.form-stock-input-container') ||  // 위치 입력 필드
          activeElement.placeholder?.includes('품목명') ||
          activeElement.placeholder?.includes('사이즈') ||
          activeElement.placeholder?.includes('제조사') ||
          activeElement.placeholder?.includes('비고') ||
          activeElement.type === 'number' ||  // 수량, 가격 필드
          activeElement.tagName === 'SELECT'  // 선택 필드
        );

        if (isGeneralInputField) {
          // 일반 입력 필드에서는 정상적인 입력 허용
          return;
        }

        // 바코드 스캐너로 인한 빠른 연속 입력이거나 특정 패턴인 경우에만 차단
        if (event.key === 'Enter' ||
          (event.key.length === 1 && /[a-zA-Z0-9]/.test(event.key) &&
            this.isLikelyBarcodeInput(event))) {
          event.preventDefault();
          event.stopPropagation();
          return false;
        }
        return;
      }

      // 수기 입력 모드일 때는 수기 입력 바코드 필드에만 입력 허용
      if (this.activeBarcodeInput === 'manual') {
        const activeElement = document.activeElement;
        const isManualBarcodeInput = activeElement?.id === 'manual-barcode-input' ||
          activeElement?.classList?.contains('barcode-form-input');

        if (!isManualBarcodeInput) {
          // 수기 입력 바코드 필드가 아니면 포커스를 수기 입력 바코드 필드로 이동
          const input = document.getElementById('manual-barcode-input');
          if (input) {
            input.focus();
          }
          return;
        }
      }

      // 기존 품목 모드일 때는 현재 포커스된 바코드 필드에만 입력 허용
      if (this.activeBarcodeInput === 'existing') {
        const activeElement = document.activeElement;

        // 바코드 입력 필드가 아니면 무시
        if (!activeElement?.classList?.contains('barcode-product-table-input') &&
          !activeElement?.id?.includes('existing-barcode-input')) {
          return;
        }

        // 현재 포커스된 필드에 해당하는 제품 찾기
        const productId = activeElement.id?.replace('existing-barcode-input-', '');
        const focusedProduct = this.filteredProducts.find(p => p.id.toString() === productId);

        if (focusedProduct) {
          // 포커스된 제품을 현재 스캔 대상으로 설정
          this.currentScanProduct = focusedProduct;
        } else {
          // 해당하는 제품을 찾을 수 없으면 무시
          return;
        }
      }

      // Enter 키가 눌렸을 때 바코드 스캔 완료로 처리
      if (event.key === 'Enter' && this.barcodeInputBuffer.trim()) {
        event.preventDefault();
        this.processBarcodeData(this.barcodeInputBuffer.trim());
        return;
      }

      // 일반 문자 입력 처리 (바코드 스캐너는 보통 빠르게 연속 입력)
      if (event.key.length === 1 && /[a-zA-Z0-9]/.test(event.key)) {
        this.barcodeInputBuffer += event.key;

        // 바코드 입력 타이머 설정 (100ms 후 자동 처리)
        clearTimeout(this.barcodeInputTimer);
        this.barcodeInputTimer = setTimeout(() => {
          if (this.barcodeInputBuffer.length >= 8) { // 최소 바코드 길이
            this.processBarcodeData(this.barcodeInputBuffer.trim());
          }
        }, 100);
      } else if (event.key === 'Backspace') {
        this.barcodeInputBuffer = this.barcodeInputBuffer.slice(0, -1);
      }
    },

    // 바코드 스캐너 입력인지 판단하는 헬퍼 메서드 개선
    isLikelyBarcodeInput() {
      const now = Date.now();
      const timeDiff = now - (this.lastInputTime || 0);
      this.lastInputTime = now;

      // 현재 포커스된 요소가 일반 입력 필드인 경우 바코드 입력이 아님
      const activeElement = document.activeElement;
      const isGeneralInputField = activeElement && (
        activeElement.closest('.form-stock-input-container') ||
        activeElement.placeholder?.includes('품목명') ||
        activeElement.placeholder?.includes('사이즈') ||
        activeElement.placeholder?.includes('제조사') ||
        activeElement.placeholder?.includes('비고') ||
        activeElement.type === 'number' ||
        activeElement.tagName === 'SELECT'
      );

      if (isGeneralInputField) {
        return false;
      }

      // 매우 빠른 연속 입력만 바코드 스캐너로 판단 (임계값을 더 엄격하게)
      return timeDiff < 30;
    },

    // 바코드 데이터 처리
    async processBarcodeData(barcode) {
      try {
        if (!barcode || barcode.trim() === '') {
          return;
        }

        const cleanBarcode = barcode.trim();

        // 바코드 입력 버퍼 초기화
        this.barcodeInputBuffer = "";
        clearTimeout(this.barcodeInputTimer);

        if (this.activeBarcodeInput === 'manual') {
          // 수기 입력 모드
          await this.handleManualBarcodeScan(cleanBarcode);
        } else if (this.activeBarcodeInput === 'existing') {
          // 기존 품목 모드
          await this.handleExistingProductBarcodeScan(cleanBarcode);
        }

      } catch (error) {
        console.error('바코드 처리 중 오류:', error);
        await this.$swal.fire('오류', '바코드 처리 중 오류가 발생했습니다.', 'error');
      }
    },

    // 수기 입력 바코드 스캔 처리
    async handleManualBarcodeScan(barcode) {
      this.manualProduct.bcode = barcode;

      await this.$swal.fire({
        title: '바코드 스캔 완료',
        text: `바코드가 입력되었습니다: ${barcode}`,
        icon: 'success',
        confirmButtonText: '확인',
        timer: 2000,
        timerProgressBar: true
      });

      this.disableBarcodeScanning();
    },

    async handleExistingProductBarcodeScan(barcode) {
      try {
        const clean = (barcode || '').trim();
        if (!clean) return;

        /* 1. DB에 바코드 존재 여부 확인 */
        const { found } = await this.searchProductByBarcode(clean);
        if (found) {
          await this.$swal.fire({
            title: '이미 등록된 바코드',
            text: `${clean} 바코드는 이미 다른 품목에 등록되어 있습니다.`,
            icon: 'info',
            confirmButtonText: '확인'
          });
          return;
        }

        /* 2. 등록 대상 선정
              - 스캔 버튼을 눌러둔(currentScanProduct) 품목이 있으면 우선
              - 없으면 바코드가 없는 첫 번째 품목 */
        let product =
          this.currentScanProduct ||
          this.filteredProducts.find(p => !p.bcode || p.bcode.trim() === '');

        if (!product) {
          await this.$swal.fire(
            '등록 실패',
            '바코드를 등록할 수 있는 품목이 없습니다.',
            'warning'
          );
          return;
        }

        /* 3. 바코드 · 위치 입력 */
        product.bcode = clean;

        const [loc1, loc2] = (this.targetLocation || '').split('-');
        product.location1 = loc1 || this.targetLocation;
        product.location2 = loc2 || '';
        this.updateProductLocation(product);

        /* 4. 업데이트 품목 저장 */
        await this.updateProduct(product);

        /* 5. 체크박스 자동 선택 */
        const pid = product.id || product.num;
        if (!this.selectedProducts.includes(pid)) this.selectedProducts.push(pid);

        /* 6. 완료 알림 */
        await this.$swal.fire({
          title: '바코드 등록 완료',
          html: `
            <p><strong>${product.name3}</strong></p>
            <p>바코드 : ${clean}</p>
            <p>재고위치 : ${this.targetLocation}</p>`,
          icon: 'success',
          confirmButtonText: '확인'
        });

      } catch (err) {
        console.error('바코드 처리 오류:', err);
        await this.$swal.fire({
          title: '바코드 등록 실패',
          text: '재고 등록위치가 지정되지 않았습니다.',
          icon: 'info',
          confirmButtonText: '확인'
        });
      } finally {
        this.currentScanProduct = null;

      }
    },

    // 바코드 검색 API
    async searchProductByBarcode(barcode) {
      try {
        const response = await this.$axios.post(
          `${this.$apiUrls.hanqmedi}/searchProductByBarcode`,
          {
            barcode: barcode,
            companykey: this.$globalState.company_key
          }
        );

        if (response.data && response.data.success) {
          return {
            found: response.data.found !== false,
            product: response.data.product
          };
        }

        return { found: false, product: null };
      } catch (error) {
        console.error('바코드 검색 중 오류:', error);
        return { found: false, product: null };
      }
    },

    // 제목 길이 제한
    truncateTitle(title, maxLength = 40) {
      return title.length > maxLength ? title.substring(0, maxLength) + '...' : title;
    },

    closeModal() {
      this.$emit("close");
    },

    handleEscKey(event) {
      if (event.key === "Escape") {
        // 이미지 검색 모달이 열려있으면 ESC 키 무시
        if (this.showImageSearchModal) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.closeModal();
      }
    },
  },
};
</script>
